import {
  ActionDescriptor,
  canDownload,
  useResourceActions,
} from '@app/shared/helpers/file';
import {
  ActionRole,
  ResourceAction,
  ResourceScope,
} from '@app/types/blackroom-app';
import { getResourceActionsFromScope } from '../utils/getResourceActionsFromScope';
import useAccount from '@app/features/Account/store/useAccount';

import { Resource } from '@app/types/blackroom-api';
import { isSpecialUserEmail } from '@shared/constants/users';

export interface ResourceFilteredActionsResult {
  allowedActions: ResourceAction[];
}

/**
 * Returns the filtered actions for a resource based on the scope and the resource's properties.
 * If you don't know the resource, pass null.
 */
export function useResourceFilteredActions({
  resources,
  scope,
}: {
  resources:
    | null
    | Pick<Resource, 'id' | 'pspdfKitId' | 'type' | 'canDownload'>[];
  scope: ResourceScope | undefined;
}): ResourceFilteredActionsResult {
  scope ??= 'my';
  const scopeActions = getResourceActionsFromScope(scope);
  const actions = useResourceActions();

  const { account, isCurrentWorkspaceAdmin, hasPermission } = useAccount();

  const userActionRole: ActionRole = isCurrentWorkspaceAdmin
    ? 'workspace-admin'
    : hasPermission('manageResources')
      ? 'workspace-editor'
      : 'user';

  const filteredActions = (
    Object.entries(actions) as [ResourceAction, ActionDescriptor][]
  ).filter(([action, descriptor]) => {
    if (action === 'download' && resources && !resources.some(canDownload)) {
      return false;
    }
    if (action === 'share' && resources && resources.length > 1) {
      return false;
    }
    if (action === 'reprocess' && !isSpecialUserEmail(account?.email)) {
      return false;
    }

    return (
      scopeActions.includes(action) &&
      (!descriptor.allowedRoles ||
        descriptor.allowedRoles.includes(userActionRole as any))
    );
  });

  return {
    allowedActions: filteredActions.map(([action]) => action),
  };
}
